// Sword of Revenge Effect (Epic, Ethereal, High-Fidelity)
// Features: 
// 1. Massive ethereal sword materializing overhead
// 2. Gathering red/golden energy essence into the central core
// 3. Devastating downward strike
// 4. Ground-shattering shockwave and screen shake
// 5. Dissipation into glowing embers

import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 1.0, // Halved sword size
    riseDuration: 600,
    gatherDuration: 800,
    strikeDuration: 250, // Slightly longer for longer travel distance
    shockwaveDuration: 600,
    dissipateDuration: 600,
    energyParticleCount: 40,
    emberCount: 60,
    paths: {
        blade: '/assets/effects/sword_of_revenge/blade.svg',
        hilt: '/assets/effects/sword_of_revenge/hilt.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.blade);
getSprite(CONFIG.paths.hilt);

// ==========================================
// PARTICLES
// ==========================================

class EnergyMote {
    x: number; y: number;
    startX: number; startY: number;
    targetX: number; targetY: number;
    delay: number; duration: number;
    lifeTime: number = 0;
    size: number;
    color: string;

    constructor(targetX: number, targetY: number) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 300 + Math.random() * 300;
        this.startX = targetX + Math.cos(angle) * dist;
        this.startY = targetY + Math.sin(angle) * dist;
        this.x = this.startX;
        this.y = this.startY;
        this.targetX = targetX + (Math.random() - 0.5) * 100;
        this.targetY = targetY + (Math.random() - 0.5) * 100;

        this.delay = Math.random() * 400;
        this.duration = 300 + Math.random() * 200;
        this.size = 2 + Math.random() * 5;

        // Colors matching the sword's core and edge gradient
        const colors = ['#f368e0', '#ff9ff3', '#ff7f50', '#ff4757', '#ffffff'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number): boolean {
        if (this.delay > 0) {
            this.delay -= dt;
            return true;
        }
        this.lifeTime += dt;
        let progress = this.lifeTime / this.duration;
        if (progress >= 1) progress = 1;

        const ease = Math.pow(progress, 3); // accelerate into sword

        this.x = this.startX + (this.targetX - this.startX) * ease;
        this.y = this.startY + (this.targetY - this.startY) * ease;

        // Add a slight swirl
        const swirlAngle = progress * Math.PI * 2;
        const swirlRadius = (1 - progress) * 50;
        this.x += Math.cos(swirlAngle) * swirlRadius;
        this.y += Math.sin(swirlAngle) * swirlRadius;

        return progress < 1;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.delay > 0) return;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.restore();
    }
}

class Ember {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;
    rotation: number;
    rotSpeed: number;

    constructor(startX: number, startY: number) {
        this.x = startX;
        this.y = startY;
        const speed = 5 + Math.random() * 15;
        const angle = -Math.PI / 2 + (Math.random() * Math.PI - Math.PI / 2); // Upward spray
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed - 5;
        this.life = 1.0;
        this.size = 3 + Math.random() * 7;

        const colors = ['#ff4757', '#ffa502', '#ffdda1', '#f368e0'];
        this.color = colors[Math.floor(Math.random() * colors.length)];

        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.3;
    }

    update(dt: number) {
        this.vy += 0.4; // Gravity
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.95; // Air resistance
        this.vy *= 0.95;
        this.life -= 0.015;
        this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.life);
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 15 * this.life;

        // Draw as tiny diamonds or shards
        ctx.beginPath();
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size / 2, 0);
        ctx.lineTo(0, this.size);
        ctx.lineTo(-this.size / 2, 0);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }
}

// ==========================================
// EFFECT STATE & RENDERER
// ==========================================

export type SwordOfRevengeEffect = {
    kind: 'swordOfRevenge'
    t: number
    sx: number; sy: number; // Caster
    tx: number; ty: number; // Target
    data?: {
        phase: 'RISE' | 'GATHER' | 'STRIKE' | 'SHOCKWAVE' | 'DISSIPATE'
        motes: EnergyMote[]
        embers: Ember[]
        attackAngle: number
        hoverX: number
        hoverY: number
    }
}

export const createSwordOfRevengeEffect = (sx: number, sy: number, tx: number, ty: number): SwordOfRevengeEffect => ({
    kind: 'swordOfRevenge', t: 0, sx, sy, tx, ty
});

export const renderSwordOfRevengeEffect = (
    eff: SwordOfRevengeEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        const dx = eff.tx - eff.sx;
        const dy = eff.ty - eff.sy;
        const attackAngle = Math.atan2(dy, dx);

        // Hover point is behind the owner's avatar (opposite to attack direction)
        const hoverX = eff.sx - Math.cos(attackAngle) * 200;
        const hoverY = eff.sy - Math.sin(attackAngle) * 200;

        const motes: EnergyMote[] = [];
        for (let i = 0; i < CONFIG.energyParticleCount; i++) {
            motes.push(new EnergyMote(hoverX, hoverY));
        }

        eff.data = {
            phase: 'RISE',
            motes,
            embers: [],
            attackAngle,
            hoverX,
            hoverY
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tRise = CONFIG.riseDuration;
    const tGather = tRise + CONFIG.gatherDuration;
    const tStrike = tGather + CONFIG.strikeDuration;
    const tShockwave = tStrike + CONFIG.shockwaveDuration;
    const tTotal = tShockwave + CONFIG.dissipateDuration;

    // Update Phase
    if (eff.t < tRise) d.phase = 'RISE';
    else if (eff.t < tGather) d.phase = 'GATHER';
    else if (eff.t < tStrike) d.phase = 'STRIKE';
    else if (eff.t < tShockwave) d.phase = 'SHOCKWAVE';
    else d.phase = 'DISSIPATE';

    ctx.save();

    // 1. Camera Shake (Impact)
    let shakeX = 0;
    let shakeY = 0;
    if (d.phase === 'SHOCKWAVE') {
        const shakeIntensity = 1 - ((eff.t - tStrike) / CONFIG.shockwaveDuration);
        if (shakeIntensity > 0) {
            shakeX = (Math.random() - 0.5) * 40 * Math.pow(shakeIntensity, 2);
            shakeY = (Math.random() - 0.5) * 40 * Math.pow(shakeIntensity, 2);
        }
    }
    ctx.translate(shakeX, shakeY);

    // ==========================================
    // RENDER: SWORD
    // ==========================================
    let swordAlpha = 0;
    let swordScale = 0.3;
    let currentX = eff.sx;
    let currentY = eff.sy;
    let glowIntensity = 0;

    // Sword's tip points DOWN at rotation 0 in standard space.
    // So if attackAngle is PI/2 (down), rotation is 0.
    const swordRotation = d.attackAngle - Math.PI / 2;

    if (d.phase === 'RISE') {
        const p = eff.t / tRise;
        swordAlpha = Math.min(1, p * 3); // fade in quickly
        const easeOut = 1 - Math.pow(1 - p, 3);

        currentX = eff.sx + (d.hoverX - eff.sx) * easeOut;
        currentY = eff.sy + (d.hoverY - eff.sy) * easeOut;
        swordScale = 0.3 + (CONFIG.scale - 0.3) * easeOut;
        glowIntensity = p * 10;

    } else if (d.phase === 'GATHER') {
        swordAlpha = 1;
        swordScale = CONFIG.scale;
        const p = (eff.t - tRise) / CONFIG.gatherDuration;

        // Floating slightly while gathering, perpendicular to the attack vector
        const floatAccel = Math.sin(p * Math.PI * 4) * 15;
        currentX = d.hoverX + Math.cos(d.attackAngle + Math.PI / 2) * floatAccel;
        currentY = d.hoverY + Math.sin(d.attackAngle + Math.PI / 2) * floatAccel;

        glowIntensity = 10 + p * 30; // Core intensely glowing

        // Render gathering motes
        d.motes = d.motes.filter(m => m.update(dt));
        d.motes.forEach(m => m.draw(ctx));

    } else if (d.phase === 'STRIKE') {
        swordAlpha = 1;
        swordScale = CONFIG.scale;
        const p = (eff.t - tGather) / CONFIG.strikeDuration;
        const easeIn = Math.pow(p, 3); // Accelerate into target

        currentX = d.hoverX + (eff.tx - d.hoverX) * easeIn;
        currentY = d.hoverY + (eff.ty - d.hoverY) * easeIn;
        glowIntensity = 40;

    } else if (d.phase === 'SHOCKWAVE') {
        swordAlpha = 1;
        swordScale = CONFIG.scale;
        currentX = eff.tx;
        currentY = eff.ty;
        glowIntensity = 40;

        // Spawn sparks once on impact
        if (d.embers.length === 0) {
            for (let i = 0; i < 30; i++) {
                spawnParticle({
                    x: eff.tx,
                    y: eff.ty,
                    vx: (Math.random() - 0.5) * 20,
                    vy: (Math.random() - 0.5) * 20 - 5,
                    life: 500 + Math.random() * 500,
                    color: Math.random() > 0.5 ? '#ff4757' : '#f368e0',
                    size: 3 + Math.random() * 4
                });
            }
        }

    } else if (d.phase === 'DISSIPATE') {
        const p = (eff.t - tShockwave) / CONFIG.dissipateDuration;
        swordAlpha = 1 - p;
        if (swordAlpha < 0) swordAlpha = 0;
        swordScale = CONFIG.scale;
        currentX = eff.tx;
        currentY = eff.ty;
        glowIntensity = 40 * (1 - p);

        // Generate shattering embers once
        if (d.embers.length === 0) {
            for (let i = 0; i < CONFIG.emberCount; i++) {
                // Embers spread around the impact site
                d.embers.push(new Ember(eff.tx + (Math.random() - 0.5) * 80, eff.ty + (Math.random() - 0.5) * 80));
            }
        }
    }

    // Draw the active Sword Body
    if (swordAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = swordAlpha;
        ctx.translate(currentX, currentY);
        ctx.rotate(swordRotation);
        ctx.scale(swordScale, swordScale);

        const drawPart = (key: 'blade' | 'hilt', yOffset: number, flip: boolean = false) => {
            const img = getSprite(CONFIG.paths[key]);
            if (img.complete && img.naturalWidth > 0) {
                const w = img.naturalWidth;
                const h = img.naturalHeight;
                if (flip) {
                    ctx.save();
                    ctx.translate(0, yOffset);
                    ctx.rotate(Math.PI);
                    ctx.drawImage(img, -w / 2, -h / 2, w, h);
                    ctx.restore();
                } else {
                    ctx.drawImage(img, -w / 2, -h / 2 + yOffset, w, h);
                }
            }
        };

        // Draw Blade with strong glow
        ctx.save();
        if (glowIntensity > 0) {
            ctx.shadowColor = '#f368e0'; // Core magenta glow
            ctx.shadowBlur = glowIntensity;
        }
        // Origin (0,0) is center of mass. Blade shifted down.
        drawPart('blade', 75);
        ctx.restore();

        // Draw Hilt (always on top, sharp shadow)
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.8)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 5;
        // Hilt shifted up to overlap with blade
        drawPart('hilt', -225, true);
        ctx.restore();

        // Blinding white core overlay during STRIKE/SHOCKWAVE
        if (d.phase === 'STRIKE' || d.phase === 'SHOCKWAVE') {
            const strikeP = d.phase === 'STRIKE'
                ? (eff.t - tGather) / CONFIG.strikeDuration
                : 1 - ((eff.t - tStrike) / CONFIG.shockwaveDuration);

            ctx.globalCompositeOperation = 'screen';
            ctx.globalAlpha = Math.max(0, strikeP);
            const img = getSprite(CONFIG.paths.blade);
            if (img.complete && img.naturalWidth > 0) {
                ctx.save();
                ctx.shadowColor = '#ffffff';
                ctx.shadowBlur = 40;
                ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2 + 75, img.naturalWidth, img.naturalHeight);
                ctx.restore();
            }
        }

        ctx.restore();
    }

    // ==========================================
    // RENDER: SHOCKWAVE / CRATER
    // ==========================================
    if (d.phase === 'SHOCKWAVE' || d.phase === 'DISSIPATE') {
        const shockT = eff.t - tStrike;
        if (shockT < 1000) {
            const p = shockT / 1000;
            const easeOut = 1 - Math.pow(1 - p, 3);

            ctx.save();
            // Floor Impact Ring
            ctx.globalAlpha = (1 - p);
            ctx.translate(eff.tx, eff.ty);
            ctx.scale(1, 0.4); // Perspective flattening

            // Core blast circle
            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 350 * CONFIG.scale, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(243, 104, 224, 0.2)'; // Magenta floor burn
            ctx.fill();

            // Expanding wave lines
            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 350 * CONFIG.scale, 0, Math.PI * 2);
            ctx.strokeStyle = '#ff9ff3';
            ctx.lineWidth = 15 * (1 - p);
            ctx.stroke();

            // Crack lines radiating outwards
            ctx.beginPath();
            for (let i = 0; i < 8; i++) {
                const angle = (i / 8) * Math.PI * 2 + (Math.random() * 0.2);
                const inner = easeOut * 50;
                const outer = easeOut * 300 * Math.random() + 50;
                ctx.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
                // Jagged line
                ctx.lineTo(Math.cos(angle) * outer * 0.5 + 20, Math.sin(angle) * outer * 0.5 - 20);
                ctx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
            }
            ctx.strokeStyle = '#ff4757';
            ctx.lineWidth = 4 * (1 - p);
            ctx.stroke();

            ctx.restore();
        }
    }

    // Render shattering embers
    if (d.phase === 'DISSIPATE' || d.phase === 'SHOCKWAVE') {
        d.embers.forEach(e => {
            e.update(dt);
            e.draw(ctx);
        });
    }

    ctx.restore();

    return eff.t < tTotal;
};
